import React, { useState, useEffect } from 'react';
import './ColumnMappingReview.css';
import MappingDashboard from '../components/columnMapping/MappingDashboard';
import MappingGrid from '../components/columnMapping/MappingGrid';
import MappingSearch from '../components/columnMapping/MappingSearch';
import ConflictResolver from '../components/columnMapping/ConflictResolver';
import { 
  ColumnMappingDetail, 
  MappingStats, 
  MappingConflict,
  MappingFilters 
} from '../types/columnMappingReview';
import { columnMappingReviewApi } from '../services/columnMappingReviewApi';

const ColumnMappingReview: React.FC = () => {
  const [mappings, setMappings] = useState<ColumnMappingDetail[]>([]);
  const [stats, setStats] = useState<MappingStats | null>(null);
  const [conflicts, setConflicts] = useState<MappingConflict[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<MappingFilters>({
    page: 1,
    limit: 50,
    sort_by: 'last_used',
    sort_order: 'desc'
  });
  const [totalPages, setTotalPages] = useState(1);
  const [showConflicts, setShowConflicts] = useState(false);

  // Load mappings based on filters
  const loadMappings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await columnMappingReviewApi.getAllMappings(filters);
      setMappings(response.mappings);
      setTotalPages(response.total_pages);
    } catch (err) {
      setError('Failed to load mappings');
      console.error('Error loading mappings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStats = async () => {
    try {
      const statsData = await columnMappingReviewApi.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  // Load conflicts
  const loadConflicts = async () => {
    try {
      const conflictsData = await columnMappingReviewApi.getConflicts();
      setConflicts(conflictsData);
    } catch (err) {
      console.error('Error loading conflicts:', err);
    }
  };

  // Initial load
  useEffect(() => {
    loadMappings();
    loadStats();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<MappingFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: newFilters.search !== prev.search ? 1 : (newFilters.page || prev.page)
    }));
  };

  // Handle mapping update
  const handleMappingUpdate = async (mappingId: string, newMetadataName: string) => {
    try {
      await columnMappingReviewApi.updateMapping(mappingId, newMetadataName);
      // Reload mappings and stats
      await loadMappings();
      await loadStats();
    } catch (err) {
      console.error('Error updating mapping:', err);
      setError('Failed to update mapping');
    }
  };

  // Handle mapping delete
  const handleMappingDelete = async (mappingId: string) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) {
      return;
    }
    
    try {
      await columnMappingReviewApi.deleteMapping(mappingId);
      // Reload mappings and stats
      await loadMappings();
      await loadStats();
    } catch (err) {
      console.error('Error deleting mapping:', err);
      setError('Failed to delete mapping');
    }
  };

  // Handle view conflicts
  const handleViewConflicts = async () => {
    if (!showConflicts && conflicts.length === 0) {
      await loadConflicts();
    }
    setShowConflicts(!showConflicts);
  };

  // Handle conflict resolution
  const handleResolveConflict = async (originalColumn: string, chosenMetadataName: string) => {
    // Find all mappings for this column
    const conflictMappings = conflicts.find(c => c.original_column === originalColumn);
    if (!conflictMappings) return;

    // Update all mappings to use the chosen metadata name
    for (const mapping of conflictMappings.mappings) {
      if (mapping.metadata_name !== chosenMetadataName) {
        await handleMappingUpdate(mapping.id, chosenMetadataName);
      }
    }

    // Reload conflicts
    await loadConflicts();
  };

  return (
    <div className="column-mapping-review">
      <div className="page-header">
        <h1>Column Mapping Review</h1>
        {stats && (
          <div className="last-review">
            Last Review: {stats.recently_modified > 0 ? 'Active' : 'No recent activity'}
          </div>
        )}
      </div>

      {stats && (
        <MappingDashboard 
          stats={stats} 
          onViewConflicts={handleViewConflicts}
        />
      )}

      <MappingSearch 
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {showConflicts ? (
        <ConflictResolver
          conflicts={conflicts}
          onResolve={handleResolveConflict}
          onClose={() => setShowConflicts(false)}
        />
      ) : (
        <MappingGrid
          mappings={mappings}
          loading={loading}
          currentPage={filters.page || 1}
          totalPages={totalPages}
          onPageChange={(page) => handleFilterChange({ page })}
          onSort={(sort_by, sort_order) => handleFilterChange({ sort_by, sort_order })}
          onEdit={handleMappingUpdate}
          onDelete={handleMappingDelete}
        />
      )}
    </div>
  );
};

export default ColumnMappingReview;